import { useQuery } from "@rocicorp/zero/react";
import { ADMIN_PLUS } from "@ano/shared";
import { queries } from "@/lib/queries";

interface WorkspaceMemberLike {
  workspace_id: string;
  user_id: string;
  role: string;
  removed_at?: number | null;
}

interface ChannelMemberLike {
  channel_id: string;
  user_id: string;
  is_channel_manager?: boolean | null;
  removed_at?: number | null;
}

/**
 * Pure: is `userId` a workspace admin (ADMIN_PLUS) OR channel manager who may
 * curate Q&A in (workspaceId, channelId)? Both checks require an ACTIVE
 * (`removed_at IS NULL`) membership — this mirrors the server's
 * `assertThreadQaAuth` so the UI surfaces curation controls to exactly the
 * users the mutator will accept. The asker case is handled by the caller.
 */
export function isThreadCurator(args: {
  workspaceMembers: readonly WorkspaceMemberLike[] | undefined;
  channelMembers: readonly ChannelMemberLike[] | undefined;
  userId: string | null | undefined;
  workspaceId: string | null | undefined;
  channelId: string | null | undefined;
}): boolean {
  const { workspaceMembers, channelMembers, userId, workspaceId, channelId } =
    args;
  if (!userId) return false;
  const isWsAdmin =
    !!workspaceId &&
    (workspaceMembers ?? []).some(
      (m) =>
        m.workspace_id === workspaceId &&
        m.user_id === userId &&
        m.removed_at == null &&
        ADMIN_PLUS.includes(m.role),
    );
  if (isWsAdmin) return true;
  return (
    !!channelId &&
    (channelMembers ?? []).some(
      (m) =>
        m.channel_id === channelId &&
        m.user_id === userId &&
        m.removed_at == null &&
        !!m.is_channel_manager,
    )
  );
}

/**
 * Whether the current user can curate Q&A (workspace admin OR channel manager)
 * in the given workspace/channel. Reads the current user's OWN membership rows
 * (`*.byUser()` — small subs Zero dedups against the sidebar's existing
 * subscriptions), so it opens no heavy new sub. The asker case is combined in
 * by the caller (`canCurate = isPublicWs && (isAsker || isCurator)`).
 */
export function useIsThreadCurator(
  workspaceId: string | null | undefined,
  channelId: string | null | undefined,
  userId: string | null | undefined,
): boolean {
  const [workspaceMembers] = useQuery(
    workspaceId ? queries.workspace_members.byUser() : null,
  );
  const [channelMembers] = useQuery(
    channelId ? queries.channel_members.byUser() : null,
  );
  return isThreadCurator({
    workspaceMembers,
    channelMembers,
    userId,
    workspaceId,
    channelId,
  });
}
